// Scheduled Orders helpers
// 90-minute express order: slot start (30-min grid) + 90 min = Expected Delivery

export const SLOT_START_HOUR = 9;          // pehla slot 09:00
export const SLOT_LAST_DELIVERY_HOUR = 19; // delivery 19:00 se late nahi
export const SLOT_INTERVAL_MIN = 30;       // start times 30 min ke gap pe
export const SLOT_PROCESSING_MIN = 90;     // express processing window
export const SLOT_MAX_DAYS_AHEAD = 60;

export const SLOT_TAG = "Scan Pending"; // dentistTag: scheduled order, scan baaki

export type SlotAvailability = "available" | "almost" | "full";

export interface DynamicSchedule {
  scheduleID: number;
  scheduleName: string;
  durationMinutes: number;
  slotIntervalMinutes: number;
  startTime: string;       // "09:00:00"
  endTime: string;         // "18:00:00"
  maxCapacity: number;
  isActive: boolean;
}

export interface GeneratedSlot {
  startTime: string;
  endTime: string;
}

// Local date -> "YYYY-MM-DD" (no UTC shift)
export const toYMD = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate()
  ).padStart(2, "0")}`;

export const getTodayYMD = (): string => toYMD(new Date());

// "2026-10-05" / "2026-10-05T00:00:00" -> "2026-10-05"
export const normalizeYMD = (value?: string | null): string =>
  value ? String(value).slice(0, 10) : "";

// "2026-10-05" -> Date (local midnight)
export const parseYMD = (ymd?: string | null): Date | null => {
  const v = normalizeYMD(ymd);
  if (!v) return null;
  const [y, m, d] = v.split("-").map(Number);
  return new Date(y, m - 1, d);
};

export const addDaysYMD = (days: number): string => {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return toYMD(d);
};

const toMinutes = (time: string): number => {
  const [h, m] = time.split(":").map(Number);
  return h * 60 + m;
};

const fromMinutes = (mins: number): string =>
  `${String(Math.floor(mins / 60)).padStart(2, "0")}:${String(mins % 60).padStart(2, "0")}`;

export const timeToMinutes = (time: string): number => {
  const [h, m] = time.split(":").map(Number);
  return h * 60 + m;
};

export const minutesToTime = (mins: number): string =>
  `${String(Math.floor(mins / 60)).padStart(2, "0")}:${String(
    mins % 60
  ).padStart(2, "0")}`;
export const generateSlots = (
  schedule?: DynamicSchedule | null
): GeneratedSlot[] => {
  if (
    !schedule ||
    schedule.durationMinutes <= 0 ||
    schedule.slotIntervalMinutes <= 0
  ) {
    return [];
  }

  const startMinutes = timeToMinutes(schedule.startTime);
  const endMinutes = timeToMinutes(schedule.endTime);

  if (endMinutes <= startMinutes) return [];

  const slots: GeneratedSlot[] = [];

  for (
    let current = startMinutes;
    current + schedule.durationMinutes <= endMinutes;
    current += schedule.slotIntervalMinutes
  ) {
    slots.push({
      startTime: minutesToTime(current),
      endTime: minutesToTime(current + schedule.durationMinutes),
    });
  }

  return slots;
};

// Start times: ["09:00", "09:30", ... "17:30"] (delivery 19:00 tak)
export const buildSlotTimes = (): string[] => {
  const times: string[] = [];
  for (
    let m = SLOT_START_HOUR * 60;
    m + SLOT_PROCESSING_MIN <= SLOT_LAST_DELIVERY_HOUR * 60;
    m += SLOT_INTERVAL_MIN
  ) {
    times.push(fromMinutes(m));
  }
  return times;
};

// "15:00" -> "16:30"  (Expected Delivery)
export const getSlotEndTime = (time?: string | null): string =>
  time ? fromMinutes(toMinutes(time) + SLOT_PROCESSING_MIN) : "";

// slot start / expected delivery as real Date
export const getSlotStartDate = (ymd?: string | null, time?: string | null): Date | null => {
  const d = parseYMD(ymd);
  if (!d) return null;
  if (time) d.setMinutes(toMinutes(time));
  return d;
};
export const getSlotEndDate = (ymd?: string | null, time?: string | null): Date | null => {
  const d = getSlotStartDate(ymd, time);
  if (d && time) d.setMinutes(d.getMinutes() + SLOT_PROCESSING_MIN);
  return d;
};

// "14:00" -> "2:00 PM"
export const formatSlotTime = (time?: string | null): string => {
  if (!time) return "";
  const [h, m] = time.split(":").map(Number);
  const suffix = h >= 12 ? "PM" : "AM";
  const hour12 = h % 12 === 0 ? 12 : h % 12;
  return `${hour12}:${String(m).padStart(2, "0")} ${suffix}`;
};

// "15:00" -> "3:00 PM – 4:30 PM"  (start – expected delivery)
export const formatSlotRange = (time?: string | null): string =>
  time ? `${formatSlotTime(time)} – ${formatSlotTime(getSlotEndTime(time))}` : "";

// "2026-10-05" -> "05 Oct 2026"
export const formatSlotDate = (ymd?: string | null): string => {
  const d = parseYMD(ymd);
  return d
    ? d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })
    : "";
};

// "Mon, 05 Oct 2026"
export const formatSlotDateLong = (ymd?: string | null): string => {
  const d = parseYMD(ymd);
  return d
    ? d.toLocaleDateString("en-GB", {
        weekday: "short",
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : "";
};

// "Today" / "Tomorrow" / "05 Oct"
export const formatSlotDay = (ymd?: string | null): string => {
  const v = normalizeYMD(ymd);
  if (v === getTodayYMD()) return "Today";
  if (v === addDaysYMD(1)) return "Tomorrow";
  const d = parseYMD(v);
  return d ? d.toLocaleDateString("en-GB", { day: "2-digit", month: "short" }) : "";
};

// Aaj ki window ka start nikal chuka ho to book nahi hogi
export const isTimePassedToday = (time: string): boolean => {
  const now = new Date();
  return toMinutes(time) <= now.getHours() * 60 + now.getMinutes();
};

// Scan upload tabhi allowed jab slot start ho chuka ho (time na ho to date se)
export const canUploadScanNow = (slotDate?: string | null, slotTime?: string | null): boolean => {
  const start = getSlotStartDate(slotDate, slotTime);
  if (!start) return true;
  return start.getTime() <= Date.now();
};

// Slot start hone me kitna time bacha (ms); 0 = shuru ho chuka
export const msUntilScanOpens = (slotDate?: string | null, slotTime?: string | null): number => {
  const start = getSlotStartDate(slotDate, slotTime);
  return start ? Math.max(0, start.getTime() - Date.now()) : 0;
};

// Expected delivery me kitna time bacha (ms)
export const msUntilDelivery = (slotDate?: string | null, slotTime?: string | null): number => {
  const end = getSlotEndDate(slotDate, slotTime);
  return end ? Math.max(0, end.getTime() - Date.now()) : 0;
};

// 90061000 -> "1d 01h 01m"
export const formatCountdown = (ms: number): string => {
  const totalMin = Math.ceil(ms / 60000);
  const d = Math.floor(totalMin / 1440);
  const h = Math.floor((totalMin % 1440) / 60);
  const m = totalMin % 60;
  const hh = String(h).padStart(2, "0");
  const mm = String(m).padStart(2, "0");
  return d > 0 ? `${d}d ${hh}h ${mm}m` : `${hh}h ${mm}m`;
};

export interface SlotGroup {
  label: string;
  icon: string; // bootstrap-icons class
  times: string[];
}

export const buildSlotGroups = (): SlotGroup[] => {
  const all = buildSlotTimes();
  const hour = (t: string) => Number(t.split(":")[0]);
  return [
    { label: "Morning", icon: "bi-sunrise", times: all.filter((t) => hour(t) < 12) },
    { label: "Afternoon", icon: "bi-sun", times: all.filter((t) => hour(t) >= 12 && hour(t) < 16) },
    { label: "Evening", icon: "bi-moon-stars", times: all.filter((t) => hour(t) >= 16) },
  ].filter((g) => g.times.length > 0);
};

// ---- Status system (UI) ----
export type SlotStatus =
  | "Scheduled"
  | "Ready for Scan"
  | "Scan Uploaded"
  | "Processing"
  | "Out for Delivery"
  | "Delivered"
  | "Cancelled"
  | "Missed"
  | "Rescheduled";

// Backend ke dentistTag + date se UI status
export const getSlotStatus = (order: any): SlotStatus => {
  const tag = String(order?.dentistTag || "").toLowerCase();
  if (tag.includes("cancel")) return "Cancelled";
  if (tag.includes("miss")) return "Missed";
  if (tag.includes("reschedul")) return "Rescheduled";
  if (tag.includes("delivered")) return "Delivered";
  if (tag.includes("out for delivery")) return "Out for Delivery";
  if (order?.dentistTag === SLOT_TAG) {
    if (!canUploadScanNow(order?.slotDate, order?.slotTime)) return "Scheduled";
    const end = getSlotEndDate(order?.slotDate, order?.slotTime);
    // 90-min window nikal gayi aur scan nahi aaya
    if (end && end.getTime() < Date.now()) return "Missed";
    return "Ready for Scan";
  }
  if (tag.includes("under review")) return "Scan Uploaded";
  return "Processing";
};

// ---- Timeline (Slot Booked -> ... -> Delivered) ----
export interface TimelineStep {
  label: string;
  note?: string;
  state: "done" | "current" | "pending";
}

export const getSlotTimeline = (order: any): TimelineStep[] => {
  const tag = String(order?.dentistTag || "").toLowerCase();
  const started = canUploadScanNow(order?.slotDate, order?.slotTime);
  const pendingScan = order?.dentistTag === SLOT_TAG;

  // kitne steps complete (1-7); agla step "current"
  let done = 1;
  if (tag.includes("delivered")) done = 7;
  else if (tag.includes("out for delivery")) done = 5;
  else if (tag.includes("mill")) done = 4;
  else if (!pendingScan) done = 3;
  else if (started) done = 2;

  const labels = [
    "Slot Booked",
    "Slot Started",
    "Scan Uploaded",
    "Processing",
    "Milling",
    "Out for Delivery",
    "Delivered",
  ];
  const notes: Record<number, string> = {
    1: formatSlotDateLong(order?.slotDate),
    2: formatSlotTime(order?.slotTime),
  };

  return labels.map((label, i) => ({
    label,
    note: notes[i + 1],
    state: i < done ? "done" : i === done ? "current" : "pending",
  }));
};
